import React from 'react'
import {
  Card,
  Button,
  Space,
  message,
  Alert,
  Typography,
  Form,
  Switch,
  InputNumber,
  Input,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@services/api'
const { Paragraph } = Typography

const FixedSettings: React.FC = () => {
  const queryClient = useQueryClient()
  const [exportLoading, setExportLoading] = React.useState(false)
  const [form] = Form.useForm()

  const { data: hbRes, isLoading: hbLoading } = useQuery({
    queryKey: ['settings-heartbeat'],
    queryFn: () => api.settings.getHeartbeatSettings(),
  })

  const hbData = (hbRes as any)?.data ?? (hbRes as any)

  React.useEffect(() => {
    if (hbData && typeof hbData === 'object' && 'enabled' in hbData) {
      form.setFieldsValue({
        enabled: hbData.enabled,
        interval_seconds: hbData.interval_seconds,
        alert_cooldown_seconds: hbData.alert_cooldown_seconds,
        alert_email: hbData.alert_email ?? '',
        status_report_interval_hours: hbData.status_report_interval_hours ?? 36,
      })
    }
  }, [hbData, form])

  const saveHb = useMutation({
    mutationFn: (values: {
      enabled: boolean
      interval_seconds: number
      alert_cooldown_seconds: number
      alert_email?: string
      status_report_interval_hours: number
    }) => api.settings.updateHeartbeatSettings(values),
    onSuccess: (res) => {
      message.success((res as any)?.message || '心跳设置已保存')
      queryClient.invalidateQueries({ queryKey: ['settings-heartbeat'] })
    },
    onError: () => message.error('保存失败'),
  })

  const resetHb = useMutation({
    mutationFn: () => api.settings.resetHeartbeatSettings(),
    onSuccess: (res) => {
      message.success((res as any)?.message || '已恢复环境变量默认值')
      queryClient.invalidateQueries({ queryKey: ['settings-heartbeat'] })
    },
    onError: () => message.error('重置失败'),
  })

  const handleExportMigrationPackage = async () => {
    try {
      setExportLoading(true)
      const result = await api.settings.downloadMigrationPackage()
      message.success(`迁移包已开始下载: ${result.filename}`)
    } catch (error: any) {
      message.error(`导出失败: ${error.message || '未知错误'}`)
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <div className="page-inner">
      <Card title="系统设置" extra={<InfoCircleOutlined />}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="全局交易所配置已废弃"
          description="交易所凭证和连接状态已迁移到 Token 维度。请在 Token 页面管理各 Token 的交易所配置。"
        />
        <Paragraph style={{ marginBottom: 0 }}>
          当前系统设置包含：交易所连接心跳、迁移导出。
        </Paragraph>

        <Card title="交易所连接心跳" size="small" style={{ marginTop: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="说明"
            description={
              <>
                对「启用中」的 Token 定期探测交易所连接；异常/恢复时可发邮件（需配置 SMTP）。
                异常/恢复告警：优先 Token「通知邮箱」，否则使用「全局告警邮箱」。
                定期状态报告：仅发往各 Token 绑定的「通知邮箱」，内容为自检正常/异常（间隔可配，默认 36 小时）。
                此处保存的配置写入数据库并覆盖环境变量；「重置」后仍按 .env 为准。
              </>
            }
          />
          <Form
            form={form}
            layout="vertical"
            style={{ maxWidth: 480 }}
            onFinish={(v) =>
              saveHb.mutate({
                enabled: v.enabled,
                interval_seconds: v.interval_seconds,
                alert_cooldown_seconds: v.alert_cooldown_seconds,
                alert_email: (v.alert_email || '').trim() || undefined,
                status_report_interval_hours: v.status_report_interval_hours,
              })
            }
          >
            <Form.Item label="启用心跳检测" name="enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="探测周期（秒）"
              name="interval_seconds"
              rules={[{ required: true, message: '必填' }]}
            >
              <InputNumber min={30} max={86400} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="异常告警冷却（秒）"
              name="alert_cooldown_seconds"
              rules={[{ required: true, message: '必填' }]}
              extra="同一故障在冷却期内不重复发邮件；恢复后会发「已恢复」。"
            >
              <InputNumber min={60} max={604800} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="状态报告间隔（小时）"
              name="status_report_interval_hours"
              rules={[{ required: true, message: '必填' }]}
              extra="每隔该时间向已配置通知邮箱的 Token 发送一封自检状态邮件（正常或异常）。"
            >
              <InputNumber min={1} max={8760} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="全局告警邮箱（可选）"
              name="alert_email"
              extra="仅用于连接异常/恢复告警；定期状态报告不使用此邮箱。"
            >
              <Input type="email" placeholder="例：ops@company.com" allowClear />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={saveHb.isPending || hbLoading}>
                  保存心跳设置
                </Button>
                <Button
                  danger
                  loading={resetHb.isPending}
                  onClick={() => {
                    resetHb.mutate()
                  }}
                >
                  清除数据库覆盖（恢复环境变量）
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card title="迁移导出" size="small" style={{ marginTop: 16 }}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="导出内容包含敏感数据"
            description="将打包当前最新代码、前端文件(含素材)、Docker配置，以及数据库快照。请妥善保管下载文件。"
          />
          <Space>
            <Button type="primary" loading={exportLoading} onClick={handleExportMigrationPackage}>
              下载迁移包 (.tar.gz)
            </Button>
          </Space>
        </Card>
      </Card>
    </div>
  )
}

export default FixedSettings
